import { createMuiTheme } from '@material-ui/core/styles';

const defaultFont = "'Kosugi Maru', 'Noto Sans JP', sans-serif";

const theme = createMuiTheme({
    palette: {
        common: {
            black: '#0a0a0a',
            white: '#fff',
        },
        primary: {
            main: '#3273dc',
            dark: '#2759a5',
            light: '#4e8ff7',
            contrastText: '#fff',
        },
        secondary: {
            main: '#f5f7fa',
            light: '#f7fafc',
            contrastText: '#363636',
        },
        info: {
            main: '#209cee',
            light: 'rgba(32, 156, 238, 0.1)',
            contrastText: '#fff',
        },
        success: {
            main: '#23d160',
            light: '#e6f9ed',
            contrastText: '#fff',
        },
        warning: {
            main: '#ffdd57',
            light: '#fff7d6',
            contrastText: '#946200',
        },
        error: {
            main: '#ff3860',
            contrastText: '#fff',
        },
        grey: {
            50: '#f5f5f5',      // $grey-light, $light
            100: '#ededed',      // $grey-lighter
            200: '#7a7a7a',      // $grey
            300: '#363636',      // $grey-dark, $dark
        },
        orange: {
            main: '#ff851b',
        },
        turquoise: {
            main: '#00d1b2',
        },
        purple: {
            main: '#b86bff',
        },
        link: {
            main: '#209cee',
        },
    },
    typography: {
        fontFamily: defaultFont,
        fontSize: 14,
        fontWeightLight: 300,
        fontWeightRegular: 400,
        fontWeightBold: 700,
        h1: {
            fontFamily: defaultFont,
            fontSize: '2.6rem',
            fontWeight: 700,
            '@media (max-width: 600px)': {
                fontSize: '2.4rem',
            },
        },
        h2: {
            fontFamily: defaultFont,
            fontSize: '2.2rem',
            fontWeight: 700,
            '@media (max-width: 600px)': {
                fontSize: '2rem',
            },
        },
        h3: {
            fontFamily: defaultFont,
            fontSize: '2rem',
            fontWeight: 700,
            '@media (max-width: 600px)': {
                fontSize: '1.8rem',
            },
        },
        h4: {
            fontFamily: defaultFont,
            fontSize: '1.7rem',
            fontWeight: 700,
        },
        h5: {
            fontFamily: defaultFont,
            fontSize: '1.6rem',
            fontWeight: 700,
        },
        h6: {
            fontFamily: defaultFont,
            fontSize: '1.4rem',
            fontWeight: 700,
        },
        body1: {
            fontFamily: defaultFont,
            fontSize: '1.8rem',
            lineHeight: 1.6,
            '@media (max-width: 600px)': {
                fontSize: '1.6rem',
            },
        },
        body2: {
            fontFamily: defaultFont,
            fontSize: '1.6rem',
            lineHeight: 1.6,
            '@media (max-width: 600px)': {
                fontSize: '1.4rem',
            },
        },
        button: {
            fontFamily: defaultFont,
            textTransform: 'none',
        },
        caption: {
            fontFamily: defaultFont,
        },
        overline: {
            fontFamily: defaultFont,
        },
    },
    shape: {
        borderRadius: 8,
    },
    overrides: {
        MuiCssBaseline: {
            '@global': {
                '@font-face': [
                    {
                        fontFamily: 'Kosugi Maru',
                        fontStyle: 'normal',
                        fontDisplay: 'swap',
                        fontWeight: 400,
                    },
                    {
                        fontFamily: 'Noto Sans JP',
                        fontStyle: 'normal',
                        fontDisplay: 'swap',
                        fontWeight: 400,
                    },
                ],
                '*': {
                    margin: 0,
                    padding: 0,
                    boxSizing: 'border-box',
                    fontFamily: defaultFont + ' !important',
                    textDecoration: 'none !important',
                },
                html: {
                    WebkitFontSmoothing: 'antialiased',
                    MozOsxFontSmoothing: 'grayscale',
                    fontSize: '62.5%',
                    fontFamily: defaultFont + ' !important',
                },
                body: {
                    margin: 0,
                    padding: 0,
                    fontSize: '1.8rem',
                    lineHeight: 1.5,
                    backgroundColor: '#fafafa',
                    fontFamily: defaultFont + ' !important',
                    '@media (max-width: 600px)': {
                        fontSize: '1.6rem',
                    },
                },
                '#root': {
                    minHeight: '100vh',
                    margin: 0,
                    padding: 0,
                },
                '.u-margin-bottom-small': { marginBottom: '1.5rem !important' },
                '.u-margin-bottom-medium': { marginBottom: '4rem !important' },
                '.u-margin-bottom-big': { marginBottom: '8rem !important' },
                '.u-margin-top-small': { marginTop: '1.5rem !important' },
                '.u-margin-top-medium': { marginTop: '4rem !important' },
                '.u-margin-top-big': { marginTop: '8rem !important' },
                '.u-margin-top-huge': { marginTop: '10rem !important' },
                '.u-margin-left-small': { marginLeft: '1.5rem !important' },
                '.u-margin-left-medium': { marginLeft: '4rem !important' },
                '.u-margin-left-big': { marginLeft: '8rem !important' },
                '.u-margin-left-huge': { marginLeft: '10rem !important' },
                '.u-margin-right-small': { marginRight: '1.5rem !important' },
                '.u-margin-right-medium': { marginRight: '4rem !important' },
                '.u-margin-right-big': { marginRight: '8rem !important' },
                '.u-margin-right-huge': { marginRight: '10rem !important' },
                '.u-margin-small': { margin: '1rem !important' },
                '.u-margin-medium': { margin: '2rem !important' },
                '.u-margin-auto': { margin: 'auto !important', display: 'block' },
                '.u-padding-small': { padding: '1rem !important' },
                '.u-padding-medium': { padding: '2rem !important' },
                '.u-flex': { display: 'flex !important' },
                '.u-flex-justify': { justifyContent: 'center !important' },
                '.u-space-between': { justifyContent: 'space-between !important' },
                '.u-space-around': { justifyContent: 'space-around !important' },
                '.u-flex-align': { alignItems: 'center !important' },
                '.u-flex-start': { alignItems: 'flex-start !important' },
                '.u-flex-end': { alignItems: 'flex-end !important' },
                '.u-flex-wrap': { flexWrap: 'wrap !important' },
                '.u-flex-grow': { flexGrow: 1 },
                '.u-flex-responsive': {
                    display: 'flex !important',
                    '@media (max-width: 900px)': {
                        flexDirection: 'column !important'
                    }
                },
                '.u-center-text': { textAlign: 'center !important' },
                '.u-text-wrap': { whiteSpace: 'pre-wrap !important' },
                '.hidden': { display: 'none !important' },
                '.modal': {
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    backgroundColor: 'rgba(0,0,0,0.4)',
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    zIndex: 1000
                },
                '.modal__message': {
                    backgroundColor: '#fff',
                    fontSize: '2.4rem',
                    padding: '3rem',
                    border: '2px solid #3273dc'
                },
                '.required::after': {
                    content: '"*"',
                    fontSize: '1.2rem',
                    marginLeft: '.5rem',
                    color: 'red'
                },
                '.link': {
                    color: '#209cee'
                },
                '.u-w100': { width: '100% !important' },
                '.u-w90': { width: '90% !important' },
                '.u-w80': { width: '80% !important' },
                '.u-w70': { width: '70% !important' },
                '.u-w60': { width: '60% !important' },
                '.u-w50': { width: '50% !important' },
                '.u-w40': { width: '40% !important' },
                '.section-title': {
                    padding: '1.5rem 2rem',
                    borderRadius: '4px',
                    backgroundColor: '#eef6fc',
                    border: '1px solid #2759a5',
                    color: '#066298',
                    marginBottom: '1rem !important',
                    fontSize: '1.8rem',
                    fontWeight: 700,
                },
            }
        },
        MuiButton: {
            root: {
                fontFamily: defaultFont + ' !important',
                fontSize: '1.6rem',
            },
            sizeLarge: {
                fontSize: '2rem',
            }
        },
        MuiToggleButton: {
            root: {
                fontFamily: defaultFont + ' !important',
                fontSize: '1.6rem',
            }
        },
        MuiTypography: {
            root: {
                fontFamily: defaultFont + ' !important',
                textDecoration: 'none !important',
                '& a': {
                    textDecoration: 'none !important',
                    color: 'inherit !important',
                },
            },
        },
        MuiInputBase: {
            root: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiMenuItem: {
            root: {
                fontFamily: defaultFont + ' !important',
            }
        },
        MuiListItem: {
            root: {
                fontFamily: defaultFont + ' !important',
            }
        },
        MuiInputLabel: {
            root: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiFormLabel: {
            root: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiTab: {
            root: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiTableCell: {
            root: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiAccordionSummary: {
            root: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiAccordionDetails: {
            root: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiDialogTitle: {
            root: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiDialogContent: {
            root: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiChip: {
            root: {
                fontFamily: defaultFont + ' !important',
                fontSize: '1.2rem',
            },
        },
        MuiTooltip: {
            tooltip: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiAlert: {
            root: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiBreadcrumbs: {
            root: {
                fontFamily: defaultFont + ' !important',
            },
        },
        MuiLink: {
            root: {
                textDecoration: 'none !important',
                color: 'inherit !important',
                '&:hover': {
                    textDecoration: 'none !important',
                },
                '&:focus': {
                    textDecoration: 'none !important',
                },
                '&:active': {
                    textDecoration: 'none !important',
                },
                '&:visited': {
                    textDecoration: 'none !important',
                },
            },
        },
    },
});

export default theme;